import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from "@/hooks/ui/use-toast";
import { supabase as supabaseClient } from '@/integrations/supabase/client';
import { DayTemplate, DayTemplateShift, DayShift } from '@/types';
import { format } from 'date-fns';

interface UseDayTemplatesProps {
  businessId: string | null;
}

interface UseDayTemplatesReturn {
  dayTemplates: DayTemplate[];
  loadingTemplates: boolean;
  editingTemplateId: string | null;
  isEditDialogOpen: boolean;
  editedTemplate: DayTemplate | null;
  editedName: string;
  editedDescription: string;
  editedShifts: DayTemplateShift[];
  setEditedName: (name: string) => void;
  setEditedDescription: (description: string) => void;
  setEditedShifts: (shifts: DayTemplateShift[]) => void;
  setIsEditDialogOpen: (isOpen: boolean) => void;
  handleEditTemplate: (templateId: string) => void;
  handleSaveTemplate: () => Promise<void>;
  handleDeleteTemplate: (templateId: string) => Promise<void>;
  closeEditDialog: () => void;
  fetchDayTemplates: () => Promise<void>;
  createDayTemplate: (name: string, description: string, shifts: DayTemplateShift[]) => Promise<void>;
  createTemplateFromDay: (name: string, description: string, dayShifts: DayShift[]) => Promise<void>;
  applyTemplateToDate: (template: DayTemplate, targetDate: Date) => DayShift[];
}

export const useDayTemplates = ({ businessId }: UseDayTemplatesProps): UseDayTemplatesReturn => {
  const [rawDayTemplates, setRawDayTemplates] = useState<DayTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Memoize dayTemplates to prevent unnecessary re-renders when the array content is the same
  const dayTemplates = useMemo(() => {
    // Create a stable reference for the templates array
    return rawDayTemplates.map(template => ({
      ...template,
      // Ensure shifts array is also stable
      shifts: template.shifts || []
    }));
  }, [rawDayTemplates]);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editedTemplate, setEditedTemplate] = useState<DayTemplate | null>(null);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedShifts, setEditedShifts] = useState<DayTemplateShift[]>([]);

  // Fetch day templates
  const fetchDayTemplates = useCallback(async () => {
    if (!businessId) {
      setRawDayTemplates([]);
      return;
    }

    setLoadingTemplates(true);

    try {
      const { data, error } = await supabaseClient
        .from('day_templates')
        .select('id, business_id, name, description, shifts, created_at, updated_at')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const newTemplates = (data || []).map((template: Record<string, unknown>) => ({
        id: template.id,
        business_id: template.business_id,
        name: template.name,
        description: template.description,
        shifts: template.shifts as DayTemplateShift[],
        created_at: template.created_at,
        updated_at: template.updated_at,
      }));
      
      // Only update if the data has actually changed (deep comparison by IDs and update times)
      setRawDayTemplates(prevTemplates => {
        const isSame = prevTemplates.length === newTemplates.length &&
          prevTemplates.every((prev, index) => {
            const current = newTemplates[index];
            return prev.id === current.id && prev.updated_at === current.updated_at;
          });
        
        return isSame ? prevTemplates : newTemplates;
      });
    } catch (error: unknown) {
      console.error('Error fetching day templates:', error);
      toast.error('Failed to fetch day templates', { description: error instanceof Error ? error.message : String(error) });
    } finally {
      setLoadingTemplates(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchDayTemplates();
  }, [fetchDayTemplates]);

  // Handle editing a day template
  const handleEditTemplate = useCallback((templateId: string) => {
    const template = dayTemplates.find(t => t.id === templateId);
    if (template) {
      setEditingTemplateId(templateId);
      setEditedTemplate(template);
      setEditedName(template.name);
      setEditedDescription(template.description || '');
      setEditedShifts(template.shifts || []);
      setIsEditDialogOpen(true);
    }
  }, [dayTemplates]);

  // Handle saving an edited day template
  const handleSaveTemplate = useCallback(async () => {
    if (!businessId || !editedTemplate) {
      toast.error('Cannot save template: No business ID provided');
      return;
    }

    if (!editedName.trim()) {
      toast.error('Template name is required');
      return;
    }

    if (editedShifts.length === 0) {
      toast.error('Template must contain at least one shift');
      return;
    }

    try {
      const { error } = await supabaseClient
        .from('day_templates')
        .update({
          name: editedName.trim(),
          description: editedDescription.trim() || null,
          shifts: editedShifts,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editedTemplate.id);

      if (error) {
        throw error;
      }

      toast.success('Day template updated successfully');
      fetchDayTemplates();
      closeEditDialog();
    } catch (error: unknown) {
      console.error('Error saving day template:', error);
      toast.error('Failed to save day template', { description: error instanceof Error ? error.message : String(error) });
    }
  }, [businessId, editedTemplate, editedName, editedDescription, editedShifts, fetchDayTemplates]);

  // Close the edit dialog
  const closeEditDialog = useCallback(() => {
    setIsEditDialogOpen(false);
    setEditingTemplateId(null);
    setEditedTemplate(null);
    setEditedShifts([]);
    setEditedName('');
    setEditedDescription('');
  }, []);

  // Handle deleting a day template
  const handleDeleteTemplate = useCallback(async (templateId: string) => {
    if (!businessId) {
      toast.error('Cannot delete template: No business ID provided');
      return;
    }

    try {
      const { error } = await supabaseClient
        .from('day_templates')
        .delete()
        .eq('id', templateId);

      if (error) {
        throw error;
      }

      toast.success('Day template deleted successfully');
      fetchDayTemplates();
    } catch (error: unknown) {
      console.error('Error deleting day template:', error);
      toast.error('Failed to delete day template', { description: error instanceof Error ? error.message : String(error) });
    }
  }, [businessId, fetchDayTemplates]);

  // Create a new day template
  const createDayTemplate = useCallback(async (name: string, description: string, shifts: DayTemplateShift[]) => {
    if (!businessId) {
      toast.error('Cannot create template: No business ID provided');
      return;
    }

    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }

    if (shifts.length === 0) {
      toast.error('Template must contain at least one shift');
      return;
    }

    try {
      const { error } = await supabaseClient
        .from('day_templates')
        .insert({
          business_id: businessId,
          name: name.trim(),
          description: description.trim() || null,
          shifts: shifts,
        });

      if (error) {
        throw error;
      }

      toast.success('Day template created successfully');
      fetchDayTemplates();
    } catch (error: unknown) {
      console.error('Error creating day template:', error);
      toast.error('Failed to create day template', { description: error instanceof Error ? error.message : String(error) });
    }
  }, [businessId, fetchDayTemplates]);

  // Convert current day shifts to a template
  const createTemplateFromDay = useCallback(async (
    name: string,
    description: string,
    dayShifts: DayShift[],
    saveEmployeeAssignments: boolean = false
  ) => {
    if (dayShifts.length === 0) {
      toast.error('No shifts to save as template');
      return;
    }

    // Convert DayShift objects to DayTemplateShift objects with relative times
    const templateShifts: DayTemplateShift[] = dayShifts.map(shift => ({
      startTime: format(shift.startTime, 'HH:mm'),
      endTime: format(shift.endTime, 'HH:mm'),
      title: shift.title || 'Shift',
      employeeRole: undefined,
      employeeId: saveEmployeeAssignments ? shift.employeeId : undefined,
      employeeName: saveEmployeeAssignments ? shift.employeeName : undefined,
      breaks: shift.breaks || [],
    }));

    await createDayTemplate(name, description, templateShifts);
  }, [createDayTemplate]);

  // Apply a template to a specific date, returning new DayShift objects
  const applyTemplateToDate = useCallback((template: DayTemplate, targetDate: Date): DayShift[] => {
    return template.shifts.map((templateShift, index) => {
      const [startHour, startMinute] = templateShift.startTime.split(':').map(Number);
      const [endHour, endMinute] = templateShift.endTime.split(':').map(Number);

      const startTime = new Date(targetDate);
      startTime.setHours(startHour, startMinute, 0, 0);

      const endTime = new Date(targetDate);
      endTime.setHours(endHour, endMinute, 0, 0);

      // Handle shifts that cross midnight
      if (endTime <= startTime) {
        endTime.setDate(endTime.getDate() + 1);
      }

      return {
        id: `template-${template.id}-${index}-${Date.now()}`, // Temporary ID
        employeeName: templateShift.employeeName || '',
        employeeId: templateShift.employeeId || undefined,
        startTime,
        endTime,
        title: templateShift.title,
        breaks: templateShift.breaks,
        isNew: true,
      };
    });
  }, []);

  return {
    dayTemplates,
    loadingTemplates,
    editingTemplateId,
    isEditDialogOpen,
    editedTemplate,
    editedName,
    editedDescription,
    editedShifts,
    setEditedName,
    setEditedDescription,
    setEditedShifts,
    setIsEditDialogOpen,
    handleEditTemplate,
    handleSaveTemplate,
    handleDeleteTemplate,
    closeEditDialog,
    fetchDayTemplates,
    createDayTemplate,
    createTemplateFromDay,
    applyTemplateToDate,
  };
};